import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from "@mui/material";

import {
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  getPermissions,
} from "../services/RoleService";
import PermissionsPicker from "../components/Role/PermissionsPicker";
import RolesTable from "../components/Role/RolesTable";

import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import AddIcon from "@mui/icons-material/Add";

import { useAuth } from "../context/AuthContext";
import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import { useToast } from "../context/ToastContext";

export default function Role() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission("crear roles");
  const canView = hasPermission("ver roles");

  const emptyForm = useMemo(() => ({ name: "", permissions: [] }), []);

  const [form, setForm] = useState(emptyForm);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formOpen, setFormOpen] = useState(false);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await getRoles();
      setRoles(res.data.data || []);
    } catch {
      showToast("Error al cargar roles.", "error")
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const res = await getPermissions();
      setPermissions(res.data.data || []);
    } catch {
      showToast("Error al cargar permisos.", "error")
    }finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditMode(false);
    setEditId(null);
    setErrors({});
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const validate = () => {
    const temp = {};
    temp.name = form.name ? "" : "Nombre requerido";
    temp.permissions =
      form.permissions.length > 0 ? "" : "Seleccione al menos un permiso";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        name: form.name,
        permissions: form.permissions.map((p) => p.id),
      };

      const res = editMode
        ? await updateRole(editId, payload)
        : await createRole(payload);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        await fetchRoles();
        closeForm();
      } else {
        showToast(res.data?.message || "Error al guardar rol.", "error")
      }
    } catch (error) {
      if (error.response?.data?.message) {
        showToast(error.response.data.message, "error")
      } else {
        showToast("Error al guardar rol.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    const selectedPermissions = permissions.filter((p) =>
      role.permissions.some((rp) => rp.id === p.id),
    );

    setForm({
      name: role.name,
      permissions: selectedPermissions,
    });
    setEditId(role.id);
    setEditMode(true);
    setErrors({});
    setFormOpen(true);
  };

  const handleOpenDeleteConfirm = (id) => {
    setSelectedDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!selectedDeleteId) return;

    try {
      const res = await deleteRole(selectedDeleteId);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success")
        setOpenDeleteConfirm(false);
        setSelectedDeleteId(null);
        fetchRoles();
      } else {
        showToast(res.data?.message || "No se pudo eliminar el rol.", "error")
      }
    } catch (error) {
      if (error.response?.data?.message) {
        showToast(error.response.data.message, "error")
      } else {
        showToast("Error al eliminar rol.", "error")
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>

        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<SupervisedUserCircleIcon />}
            title="Roles"
            description="Administrar roles y permisos del sistema."
            action={
              canCreate ? (
                <Button
                  onClick={openCreate}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Nuevo rol
                </Button>
              ) : null
            }
          />
        </Grid>

        {canView && (
          <Grid size={{ xs: 12 }}>
            <RolesTable
              roles={roles}
              onEdit={handleEdit}
              onDelete={handleOpenDeleteConfirm}
              loading={loading}
            />
          </Grid>
        )}
      </Grid>

      <AppDialog
        open={formOpen}
        onClose={closeForm}
        maxWidth="sm"
        title={editMode ? "Editar rol" : "Nuevo rol"}
        subtitle={
          editMode
            ? "Actualizá el nombre y permisos del rol."
            : "Creá un rol y asignale permisos."
        }
        icon={<SupervisedUserCircleIcon />}
        actions={null}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.2,
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontWeight: 800, opacity: 0.85 }}>
                  Datos del rol
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nombre del Rol"
                name="name"
                value={form.name}
                onChange={(e) => {
                  setForm({ ...form, name: e.target.value });
                  setErrors({ ...errors, name: "" });
                }}
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <PermissionsPicker
                options={permissions}
                value={form.permissions}
                onChange={(newValue) => {
                  setForm({ ...form, permissions: newValue });
                  setErrors({ ...errors, permissions: "" });
                }}
                error={!!errors.permissions}
                helperText={errors.permissions}
                height={320}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                endIcon={<SendIcon />}
                disabled={loading}
                sx={{ py: 1.4, borderRadius: 2, fontWeight: 800 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : editMode ? (
                  "Actualizar"
                ) : (
                  "Registrar"
                )}
              </Button>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                onClick={closeForm}
                fullWidth
                endIcon={<CancelIcon />}
                disabled={loading}
                sx={{ py: 1.2, borderRadius: 2 }}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setSelectedDeleteId(null);
        }}
        onConfirm={handleDelete}
        title="Confirmar eliminación"
        description="¿Eliminar este rol? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />
    </Container>
  );
}
